import React from 'react';

import { extractAllocationStatistics } from '../utils';
import messages from './messages';
import getSymbolFromCurrency from 'currency-symbol-map';

import { FormattedMessage, FormattedDate, FormattedNumber } from 'react-intl';
import Table from 'components/Table';
import Tooltip from 'components/Tooltip';

const { Column, ColumnGroup } = Table;

const BlockAllocationsTable = ({ allocations, currency }) => {
    /**
     * Renders a formatted message for a column header. If a tooltip message is provided,
     * renders the header with one. The FormattedMessage needs to be wrapper with an
     * html native element for the tooltip to work
     * @param  {Object} headerMessage
     * @param  {?Object} [tooltipMessage=null]
     */
    const renderColumnHeader = (headerMessage, tooltipMessage = null) => {
        const formattedHeader = <FormattedMessage {...headerMessage} />;
        if (!tooltipMessage) return formattedHeader;

        return (
            <Tooltip title={<FormattedMessage {...tooltipMessage} />}>
                <div>{formattedHeader}</div>
            </Tooltip>
        );
    };

    const renderDate = (date) => (
        <FormattedDate value={date} weekday="short" day="2-digit" month="short" year="2-digit" />
    );

    const renderRoomCount = (rooms) => <FormattedNumber value={rooms} />;

    const renderRevenue = (revenue) => (
        <>
            {getSymbolFromCurrency(currency)}
            <FormattedNumber value={revenue / 100} minimumFractionDigits={0} maximumFractionDigits={0} />
        </>
    );

    /**
     * Generate a Column's "render" func depending on the type of data
     * @param  {String} type - "rooms" or "revenue" - to choose a formatter
     * @param  {String} field - one of the pre-defined allocations stats fields
     * @return {Function} "render" function for a column
     */
    const makeCellRenderer = (type, field) => (_cellValue, allocation) => {
        let renderFunc = renderRoomCount;
        if (type === 'revenue') renderFunc = renderRevenue;
        return renderFunc(extractAllocationStatistics(allocation)[field]);
    };

    return (
        <Table
            size="small"
            dataSource={allocations}
            rowKey="stayDate"
            tableLayout="auto"
            pagination={{
                hideOnSinglePage: true,
                pageSize: 5,
                showSizeChanger: false,
            }}
        >
            <Column
                title={renderColumnHeader(messages.dateColumnHeader)}
                dataIndex="stayDate"
                key="stayDate"
                render={renderDate}
            />

            <ColumnGroup title={renderColumnHeader(messages.roomsGroupColumnHeader)}>
                <Column
                    title={renderColumnHeader(messages.roomsBlockedColumnHeader)}
                    key="blockedRoomCount"
                    render={makeCellRenderer('room', 'blockedRoomCount')}
                />
                <Column
                    title={renderColumnHeader(messages.roomsSoldColumnHeader, messages.roomsSoldColumnHeaderTooltip)}
                    key="roomsSold"
                    render={makeCellRenderer('room', 'roomsSold')}
                />
                <Column
                    title={renderColumnHeader(messages.roomsBlockedColumnHeader)}
                    key="blockedRoomCount"
                    render={makeCellRenderer('room', 'blockedRoomCount')}
                />
                <Column
                    title={renderColumnHeader(messages.roomsSoldColumnHeader, messages.roomsSoldColumnHeaderTooltip)}
                    key="roomsSold"
                    render={makeCellRenderer('room', 'roomsSold')}
                />
                <Column
                    title={renderColumnHeader(
                        messages.roomsAvailableColumnHeader,
                        messages.roomsAvailableColumnHeaderTooltip,
                    )}
                    key="availableRoomCount"
                    render={makeCellRenderer('room', 'availableRoomCount')}
                />
            </ColumnGroup>

            <ColumnGroup title={renderColumnHeader(messages.revenueGroupColumnHeader)}>
                <Column
                    title={renderColumnHeader(
                        messages.revenueSoldColumnHeader,
                        messages.revenueSoldColumnHeaderTooltip,
                    )}
                    key="revenueSold"
                    render={makeCellRenderer('revenue', 'revenueSold')}
                />
                <Column
                    title={renderColumnHeader(
                        messages.revenueTotalColumnHeader,
                        messages.revenueTotalColumnHeaderTooltip,
                    )}
                    key="totalRevenueEstimate"
                    render={makeCellRenderer('revenue', 'estimatedRevenueTotal')}
                />
            </ColumnGroup>
        </Table>
    );
};

export default BlockAllocationsTable;
